import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { getConsent, setConsent } from "../utils/consent";

const DEFAULTS = { necessary: true, maps: false, analytics: false };

function consentToPrefs(c) {
  if (!c) return DEFAULTS;
  return {
    necessary: true,
    maps: !!c.maps,
    analytics: !!c.analytics,
  };
}

export default function CookieBanner() {
  // Direkt im Initial-State entscheiden, damit kein Frame verloren geht
  const [visible, setVisible] = useState(() => !getConsent());
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState(() => consentToPrefs(getConsent()));

  useEffect(() => {
    // Sicherheits-Fallback falls beim ersten Render noch nichts angezeigt wurde
    if (!getConsent()) setVisible(true);
  }, []);

  // Banner kann via Footer-Link erneut geöffnet werden – dann Prefs aus Storage neu laden
  useEffect(() => {
    const reopen = () => {
      setPrefs(consentToPrefs(getConsent()));
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener("consent-reset", reopen);
    window.addEventListener("open-cookie-banner", reopen);
    return () => {
      window.removeEventListener("consent-reset", reopen);
      window.removeEventListener("open-cookie-banner", reopen);
    };
  }, []);

  const acceptAll = () => {
    const next = { necessary: true, maps: true, analytics: true };
    setPrefs(next);
    setConsent(next);
    setVisible(false);
  };

  const rejectAll = () => {
    const next = { necessary: true, maps: false, analytics: false };
    setPrefs(next);
    setConsent(next);
    setVisible(false);
  };

  const saveCustom = () => {
    const next = { ...prefs, necessary: true };
    setConsent(next);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-modal="true" data-testid="cookie-banner">
      <div className="cookie-card">
        <div className="cookie-head">
          <h3>Datenschutz-Einstellungen</h3>
          <p>
            Wir verwenden Cookies und ähnliche Technologien. Einige sind notwendig für den
            Betrieb der Website, andere helfen uns, externe Inhalte (z.&nbsp;B. Google Maps)
            anzuzeigen. Sie entscheiden selbst, was Sie erlauben. Details siehe{" "}
            <Link to="/datenschutz" onClick={() => setVisible(false)}>Datenschutz</Link>.
          </p>
        </div>

        {expanded && (
          <div className="cookie-categories">
            <label className="cookie-cat" data-testid="cookie-cat-necessary">
              <div>
                <strong>Notwendig</strong>
                <span>Für den Betrieb der Website erforderlich (immer aktiv).</span>
              </div>
              <input type="checkbox" checked readOnly disabled />
            </label>
            <label className="cookie-cat" data-testid="cookie-cat-maps">
              <div>
                <strong>Karten (Google Maps)</strong>
                <span>Anzeige der eingebetteten Karte. Setzt Cookies von Google.</span>
              </div>
              <input
                type="checkbox"
                checked={prefs.maps}
                onChange={(e) => setPrefs((p) => ({ ...p, maps: e.target.checked }))}
              />
            </label>
            <label className="cookie-cat" data-testid="cookie-cat-analytics">
              <div>
                <strong>Statistik (optional)</strong>
                <span>Anonyme Nutzungsstatistiken zur Verbesserung der Website.</span>
              </div>
              <input
                type="checkbox"
                checked={prefs.analytics}
                onChange={(e) => setPrefs((p) => ({ ...p, analytics: e.target.checked }))}
              />
            </label>
          </div>
        )}

        <div className="cookie-actions">
          {!expanded && (
            <button
              className="btn btn-outline"
              onClick={() => setExpanded(true)}
              data-testid="cookie-settings-btn"
            >
              Einstellungen <ChevronDown size={14} />
            </button>
          )}
          {expanded && (
            <button
              className="btn btn-outline"
              onClick={saveCustom}
              data-testid="cookie-save-btn"
            >
              Auswahl speichern
            </button>
          )}
          <button
            className="btn btn-outline"
            onClick={rejectAll}
            data-testid="cookie-reject-btn"
          >
            Ablehnen
          </button>
          <button
            className="btn btn-gold"
            onClick={acceptAll}
            data-testid="cookie-accept-btn"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
}
